import Modal from "components/modal";
import Button from "components/button";
import React, { useMemo, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import WithoutPageTable from "components/table/WithoutPageTable";
import { MdCheckCircle, MdOutlineError } from "react-icons/md";
import { CONTAINER_ITEM_MAPPING_STATUS } from "constants";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

const columnHelper = createColumnHelper();
function ViewModal({ containerDetails }) {
  const { container: containerPharm = {} } = useSelector(
    (state) => state.auth.permissions || {}
  );
  const [isOpen, setIsOpen] = useState(false);
  const openModal = () => setIsOpen(true);
  const closeModal = () => {
    setIsOpen(false);
  };
  const containerItemsData = useMemo(
    () =>
      containerDetails.items?.filter((item) => item._id)?.map((item) => item) ??
      [],
    [containerDetails]
  );

  const columns = React.useMemo(() => {
    return [
      // ORDER NO (safer accessor for nested)

      columnHelper.accessor((row) => row?.order?.orderNo, {
        id: "orderNo",
        header: "ORDER NO",
        enableSorting: false,
        enableHiding: false,

        cell: (info) => {
          const row = info.row.original;
          const orderId = row?.order?._id;
          const orderNo = info.getValue();
          const userName = `${row?.order?.orderForInfo?.firstname || ""} ${
            row?.order?.orderForInfo?.lastname || ""
          }`.trim();

          return orderId ? (
            <div className="flex flex-col">
              <Link
                to={`/orders/${orderId}`}
                className="cursor-pointer text-blue-600 hover:underline"
              >
                {orderNo || "N/A"}
              </Link>
              {userName && <span className="text-sm">{userName}</span>}
            </div>
          ) : (
            "N/A"
          );
        },
      }),

      // IMAGE
      columnHelper.accessor("picture", {
        id: "image",
        header: "IMG",
        enableHiding: false,
        enableSorting: false,
        cell: ({ getValue }) => {
          const url = getValue();
          return url ? (
            <img
              src={url}
              alt="item"
              className="h-12 w-12 rounded object-cover ring-1 ring-gray-200"
              onError={(e) => (e.currentTarget.style.visibility = "hidden")}
            />
          ) : (
            <span className="text-gray-400">—</span>
          );
        },
      }),

      // BASIC FIELDS
      columnHelper.accessor("name", {
        id: "name",
        header: "NAME",
        enableSorting: false,
        enableHiding: false,

        cell: (info) => info.getValue() || "-",
      }),
      columnHelper.accessor("marka", {
        id: "marka",
        header: "MARKA",
        enableHiding: false,

        enableSorting: false,
        cell: (info) => info.getValue() || "-",
      }),
      columnHelper.accessor("description", {
        id: "description",
        header: "DESCRIPTION",
        enableSorting: false,
        cell: (info) => info.getValue() || "-",
      }),
      columnHelper.accessor("ctn", {
        id: "ctn",
        header: "CTN",
        enableSorting: false,
        enableHiding: false,

        cell: (info) => info.getValue() ?? 0,
      }),
      columnHelper.accessor("qty", {
        id: "qty",
        header: "QTY/CTN",
        enableHiding: false,

        enableSorting: false,
        cell: (info) => info.getValue() ?? 0,
      }),
      columnHelper.accessor("kg", {
        id: "kg",
        header: "KG/CTN",
        enableSorting: false,
        cell: (info) => info.getValue() ?? 0,
      }),
      columnHelper.accessor("cbm", {
        id: "cbm",
        header: "CBM/CTN",
        enableSorting: false,
        cell: (info) => info.getValue() ?? 0,
      }),
      columnHelper.accessor("rate", {
        id: "rate",
        header: "RATE",
        enableSorting: false,
        cell: (info) => info.getValue() ?? 0,
      }),
      columnHelper.accessor("totalQty", {
        id: "totalQty",
        header: "TOTAL QTY",
        enableHiding: false,

        enableSorting: false,
        cell: (info) => info.getValue() ?? 0,
      }),
      columnHelper.accessor("totalKg", {
        id: "totalKg",
        header: "TOTAL KG",
        enableSorting: false,
        cell: (info) => info.getValue() ?? 0,
      }),
      columnHelper.accessor("totalCBM", {
        id: "totalCBM",
        header: "TOTAL CBM",
        enableSorting: false,
        cell: (info) => info.getValue() ?? 0,
      }),
      columnHelper.accessor("totalAmount", {
        id: "totalAmount",
        header: "TOTAL AMOUNT",
        enableSorting: false,
        cell: (info) => info.getValue() ?? 0,
      }),

      // STATUS

      columnHelper.accessor((row) => row.mappingStatus?.enumVal || "", {
        id: "mappingStatus",
        header: "STATUS",
        enableSorting: false,
        enableHiding: false,

        cell: (info) => {
          const status = info.row.original.mappingStatus;
          if (!status) return "-";

          const statusKey = status.enumVal;
          const statusName = status.name || "-";

          return (
            <div className="flex items-center">
              {statusKey === CONTAINER_ITEM_MAPPING_STATUS.CLEARED ? (
                <MdCheckCircle className="me-1 text-green-500 dark:text-green-300" />
              ) : statusKey === CONTAINER_ITEM_MAPPING_STATUS.SUSPENSE ? (
                <MdOutlineError className="me-1 text-amber-500 dark:text-amber-300" />
              ) : null}

              <p className="text-sm font-bold text-navy-700 dark:text-white">
                {statusName}
              </p>
            </div>
          );
        },
      }),
    ];
  }, []);
  const initialColumnVisibility = {
    orderNo: containerPharm?.orderColumn ? true : false,
    image: true,
    name: true,
    marka: true,
    description: false,
    ctn: true,
    qty: true,
    kg: false,
    cbm: false,
    totalQty: true,
    totalKg: false,
    totalCBM: false,
    rate: false,
    totalAmount: false,
    mappingStatus: containerPharm?.statusColumn ? true : false,
  };

  return (
    <div>
      <Button variant="outline" className="py-1 text-sm" onClick={openModal}>
        View
      </Button>
      <Modal isOpen={isOpen} onClose={closeModal} className="max-w-[85%]">
        <h2 className="border-b py-3 pl-2 text-xl font-bold text-navy-700 dark:text-white">
          Loaded Items #{containerDetails.containerNo}
        </h2>

        <div className="custom-scrollbar mb-5 max-h-[75vh] w-full flex-col items-center overflow-y-auto lg:pl-0">
          <WithoutPageTable
            columns={columns}
            data={containerItemsData ?? []}
            initialColumnVisibility={initialColumnVisibility}
            isColumnDropdown={true}
          />
        </div>
      </Modal>
    </div>
  );
}

export default ViewModal;
